/**
 * Centralized cache strategy configuration
 * Provides consistent TTL values across the application
 * 
 * Updated with TypeScript-style enums and enhanced type safety
 */

// Cache strategy types for better type safety
export const CacheStrategyType = {
  STATIC: 'STATIC',
  DYNAMIC: 'DYNAMIC', 
  REALTIME: 'REALTIME',
  TEMPORARY: 'TEMPORARY',
  NOTIFICATIONS: 'NOTIFICATIONS'
};

// Cache durations in milliseconds
// Note: gcTime is the React Query v5 name (renamed from cacheTime)
export const CACHE_STRATEGIES = {
  // Static data that rarely changes (1 hour)
  STATIC: {
    staleTime: 60 * 60 * 1000,     // 1 hour
    gcTime: 2 * 60 * 60 * 1000,    // 2 hours
    description: 'User profiles, organization settings, system config'
  },

  // Dynamic data that changes moderately (10 minutes)
  DYNAMIC: {
    staleTime: 10 * 60 * 1000,     // 10 minutes
    gcTime: 30 * 60 * 1000,        // 30 minutes
    description: 'Connections, tables list, metadata schema'
  },

  // Real-time data that changes frequently (2 minutes)
  REALTIME: {
    staleTime: 2 * 60 * 1000,      // 2 minutes
    gcTime: 10 * 60 * 1000,        // 10 minutes
    description: 'Automation status, metadata status, validation results'
  },

  // Temporary data for short operations (30 seconds)
  TEMPORARY: {
    staleTime: 30 * 1000,          // 30 seconds
    gcTime: 2 * 60 * 1000,         // 2 minutes
    description: 'Running operations, temporary calculations'
  },

  // Notifications - optimized from our previous work (5 minutes)
  NOTIFICATIONS: {
    staleTime: 5 * 60 * 1000,      // 5 minutes
    gcTime: 15 * 60 * 1000,        // 15 minutes
    description: 'Notification counts and recent notifications'
  }
};

/**
 * Get cache strategy by data type
 * @param {string} dataType - Type of data (static, dynamic, realtime, temporary, notifications)
 * @returns {Object} Cache configuration object
 */
export const getCacheStrategy = (dataType) => {
  const strategy = CACHE_STRATEGIES[dataType.toUpperCase()];
  if (!strategy) {
    console.warn(`Unknown cache strategy: ${dataType}. Using DYNAMIC as fallback.`);
    return CACHE_STRATEGIES.DYNAMIC;
  }
  return strategy;
};

/**
 * Helper function to create React Query options with standardized cache strategy
 * @param {string} dataType - Type of data for cache strategy (e.g., 'validations-summary', 'metadata-status')
 * @param {Object} customOptions - Custom options to override defaults
 * @returns {Object} React Query options
 */
export const createQueryOptions = (dataType, customOptions = {}) => {
  // Look up the cache strategy type from DATA_TYPE_MAPPING first
  const strategyType = DATA_TYPE_MAPPING[dataType] || 'DYNAMIC';
  const strategy = getCacheStrategy(strategyType);

  return {
    staleTime: strategy.staleTime,
    gcTime: strategy.gcTime,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 3000,
    // refetchOnMount: true honors staleTime — fresh cached data is reused
    // without refetching, but stale data refetches on mount so users see
    // updates after backend mutations (Tier 4 runs, validation triggers, etc.)
    // without needing a browser hard-refresh.
    refetchOnMount: true,
    refetchOnReconnect: false,
    ...customOptions
  };
};

/**
 * Mapping of data types to their appropriate cache strategies
 */
export const DATA_TYPE_MAPPING = {
  // Static data
  'user-profile': 'STATIC',
  'organization-settings': 'STATIC',
  'system-config': 'STATIC',
  
  // Dynamic data  
  'connections': 'DYNAMIC',
  'tables': 'DYNAMIC',
  'metadata-schema': 'DYNAMIC',
  'columns': 'DYNAMIC',
  
  // Real-time data
  'automation-status': 'REALTIME', 
  'metadata-status': 'REALTIME',  // Fixed: was causing Unknown cache strategy warnings
  'validation-results': 'REALTIME',
  'validations': 'REALTIME',
  'validations-summary': 'REALTIME',  // Fixed: was causing Unknown cache strategy warnings
  'table-validations': 'REALTIME',
  'next-run-times': 'REALTIME',
  
  // Temporary data
  'running-validations': 'TEMPORARY',
  'data-preview': 'TEMPORARY',
  'statistics-calculation': 'TEMPORARY',
  
  // Notifications
  'notifications': 'NOTIFICATIONS',
  'notification-count': 'NOTIFICATIONS'
};

/**
 * Get appropriate cache strategy for a specific query key
 * @param {string|Array} queryKey - The query key or data type (can be array from React Query)
 * @returns {Object} Cache strategy configuration
 */
export const getStrategyForQuery = (queryKey) => {
  // Handle React Query array format - use first element as the key
  const key = Array.isArray(queryKey) ? queryKey[0] : queryKey;
  const dataType = DATA_TYPE_MAPPING[key] || 'DYNAMIC';
  return getCacheStrategy(dataType);
};